// client/src/components/team/AssignAccountsModal.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Users, Search, Loader2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { wholesalerAPI } from '../../services/api';

const AssignAccountsModal = ({ isOpen, onClose, member, onAssigned }) => {
  const { t } = useTranslation('wholesaler');
  const [businesses, setBusinesses] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSearch('');

    // Load approved businesses
    wholesalerAPI.getCatalogRequests()
      .then((res) => {
        const approved = res.data.approvedBusinesses || [];
        setBusinesses(approved);
        // Pre-select currently assigned
        const current = new Set((member?.assignedBusinessIds || []).map(id =>
          typeof id === 'object' ? id._id || id.toString() : id
        ));
        setSelected(current);
      })
      .catch(() => {
        toast.error('Failed to load businesses');
      })
      .finally(() => setLoading(false));
  }, [isOpen, member]);

  if (!isOpen || !member) return null;

  const toggleBusiness = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await wholesalerAPI.assignAccounts(member._id, [...selected]);
      toast.success(t('team.assignAccounts.success', 'Accounts assigned'));
      onAssigned();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign accounts');
    } finally {
      setSaving(false);
    }
  };

  const filtered = businesses.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = (b.businessName || b.businessId?.businessName || '').toLowerCase();
    return name.includes(q);
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('team.assignAccounts.title', 'Assign Business Accounts')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Info banner */}
        <div className="px-6 pt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex gap-2">
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <span>{t('team.assignAccounts.hint', 'If no accounts are selected, this member can see all active accounts.')}</span>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('team.assignAccounts.searchPlaceholder', 'Search businesses...')}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Business list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-purple-500" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              {t('team.assignAccounts.noBusinesses', 'No approved businesses found')}
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map((b) => {
                const id = (b.businessId?._id || b.businessId || '').toString();
                const name = b.businessName || b.businessId?.businessName || id;
                const isChecked = selected.has(id);
                return (
                  <label
                    key={id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      isChecked ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleBusiness(id)}
                      className="text-purple-600 rounded"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 rounded-b-xl">
          <span className="text-xs text-gray-500">
            {t('team.assignAccounts.selectedCount', { count: selected.size })}
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common:cancel', 'Cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Users size={14} />
              {saving ? t('team.assignAccounts.saving', 'Saving...') : t('team.assignAccounts.save', 'Save Assignments')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignAccountsModal;
